import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, mergeMap, filter, tap } from 'rxjs';
import { catchError, map, withLatestFrom } from 'rxjs/operators';
import { AuthService } from '@core/services/auth/auth.service';
import { ModalController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { selectAuthForm, selectAuth, selectUserForm } from '@store/selectors';

import {
  AUTH_SING_UP,
  AUTH_LOG_IN,
  AUTH_UPDATE_LOADING,
  AUTH_FAILED,
  AUTH_FIND_SUCCESS,
  AUTH_RESET_PASSWORD,
  NOTIFICATION_SHOW,
  AUTH_SING_UP_FAILURE,
  AUTH_SING_UP_SUCCESS,
  SERVICE_FAILURE,
  USER_FIND,
  USER_UPDATE_COMPLETED,
  USER_UPDATE_LOADING,
  USER_FAILED,
  USER_UPDATE,
  AUTH_CHANGE_PASSWORD,
  AUTH_REFRESH_TOKEN,
  AUTH_LOGOUT,
  CLIENT_FIND_SUCCESS,
} from '@store/actions';
import { AuthenticationForm, Authentication, Client, ClientUserForm } from '@core/models';
import { NotificationEnum } from '@shared/components/notification/enum/notification.enum';

@Injectable()
export class AuthEffects {
  constructor(
    private store: Store,
    private actions$: Actions,
    private authService: AuthService,
    private modalCtrl: ModalController,
    private cookieService: CookieService
  ) {}

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AUTH_LOG_IN),
      withLatestFrom(this.store.select(selectAuthForm)),
      filter(([_, authForm]) => !!authForm),
      map(([_, authForm]) => {
        return { authForm: authForm as AuthenticationForm };
      }),
      mergeMap(({ authForm }) => {
        this.store.dispatch(AUTH_UPDATE_LOADING({ loading: true }));

        return this.authService.authenticate(authForm).pipe(
          mergeMap(({ data }) => {
            // Guardar el token en una cookie HTTP segura
            const expiryDate = new Date();
            expiryDate.setHours(expiryDate.getHours() + 2);
            this.cookieService.set(
              'access-token',
              data.token,
              expiryDate,
              '/',
              '',
              true, // HttpOnly
              'Strict' // SameSite
            );

            return [
              AUTH_FIND_SUCCESS({ auth: data }),
              USER_FIND(),
              AUTH_UPDATE_LOADING({ loading: false }),
              NOTIFICATION_SHOW({
                notification: {
                  type: NotificationEnum.Success,
                  title: NotificationEnum.Success,
                  detail: NotificationEnum.LoginSuccessfullyUpdated,
                },
              }),
            ];
          }),
          catchError(({error}) => {

            this.store.dispatch(AUTH_UPDATE_LOADING({ loading: false }));
            return of(NOTIFICATION_SHOW({ notification: { type: NotificationEnum.Error, title: NotificationEnum.Error, detail: error?.error ?? error.message[0] } }));
          })
        );
      })
    )
  );

  register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AUTH_SING_UP),
      withLatestFrom(this.store.select(selectAuthForm)),
      filter(([_, authForm]) => !!authForm),
      map(([_, authForm]) => {
        return { authForm: authForm as AuthenticationForm };
      }),
      mergeMap(({ authForm }) => {
        this.store.dispatch(AUTH_UPDATE_LOADING({ loading: true }));

        return this.authService.register(authForm).pipe(
          mergeMap(({ data }) => {
            return [
              AUTH_SING_UP_SUCCESS({ registered: true }),
              AUTH_UPDATE_LOADING({ loading: false }),
              NOTIFICATION_SHOW({
                notification: {
                  type: NotificationEnum.Success,
                  title: NotificationEnum.Success,
                  detail: NotificationEnum.AccountCreatedSuccessfully,
                },
              }),
            ];
          }),
          catchError((error) => {
            return [
              AUTH_SING_UP_FAILURE({ error: error.message }),
              AUTH_UPDATE_LOADING({ loading: false }),
              NOTIFICATION_SHOW({ notification: { type: NotificationEnum.Error, title: NotificationEnum.Error, detail: error.error.error } })
            ];
          })
        );
      })
    )
  );

  signInSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AUTH_FIND_SUCCESS),
        tap(async () => {

          const topModal = await this.modalCtrl.getTop();
          if (topModal) {
            await this.modalCtrl.dismiss('continueAsGuest',undefined, 'authModal');
          }
        })
      ),
    { dispatch: false }
  );

  refeshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AUTH_REFRESH_TOKEN),
      filter(()=>!!this.authService.getToken()),
      mergeMap(() => {
        this.store.dispatch(AUTH_UPDATE_LOADING({ loading: true }));

        return this.authService.refeshToken().pipe(
          mergeMap(({ data }) => {
            return [
              AUTH_FIND_SUCCESS({ auth: data }),
              USER_FIND(),
              AUTH_UPDATE_LOADING({ loading: false }),
            ];
          }),
          catchError(() => {
            this.store.dispatch(AUTH_LOGOUT());
            return of(AUTH_UPDATE_LOADING({ loading: false }));
          })
        );
      })
    )
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AUTH_LOGOUT),
        tap(() => {
          this.authService.logout();
        })
      ),
    { dispatch: false }
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AUTH_CHANGE_PASSWORD),
      filter(({ password, token }) => !!password && !!token),
      mergeMap(({ password, token }) => {
        this.store.dispatch(AUTH_UPDATE_LOADING({ loading: true }));

        return this.authService.changePasswordWithToken(password, token).pipe(
          mergeMap(() => {
            return [AUTH_UPDATE_LOADING({ loading: false })];
          }),
          catchError(({ error }) => {
            this.store.dispatch(AUTH_UPDATE_LOADING({ loading: false }));
            this.store.dispatch(
              SERVICE_FAILURE({
                notification: {
                  type: NotificationEnum.Error,
                  title: NotificationEnum.Error,
                  detail: error.message ? error.message : NotificationEnum.NetworkError,
                },
              })
            );
            return of(AUTH_FAILED({ error }));
          })
        );
      })
    )
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AUTH_RESET_PASSWORD),
      withLatestFrom(this.store.select(selectAuthForm)),
      filter(([_, authForm]) => !!authForm),
      map(([_, authForm]) => {
        return { authForm: authForm as AuthenticationForm };
      }),
      mergeMap(({ authForm }) => {
        this.store.dispatch(AUTH_UPDATE_LOADING({ loading: true }));

        return this.authService.requestResetPassword(authForm).pipe(
          mergeMap(({ data }) => {
            return [
              AUTH_FIND_SUCCESS({ auth: data }),
              AUTH_UPDATE_LOADING({ loading: false }),
              NOTIFICATION_SHOW({
                notification: {
                  type: NotificationEnum.Success,
                  title: NotificationEnum.Success,
                  detail: NotificationEnum.EmailSentSuccessfully,
                },
              }),
            ];
          }),
          catchError(({ error }) => {
            this.store.dispatch(AUTH_UPDATE_LOADING({ loading: false }));
            this.store.dispatch(
              SERVICE_FAILURE({
                notification: {
                  type: NotificationEnum.Error,
                  title: NotificationEnum.Error,
                  detail:  error.message ? error.message : NotificationEnum.NetworkError
                },
              })
            );
            return of(AUTH_FAILED({ error }));
          })
        );
      })
    )
  );

  updateComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(USER_UPDATE_COMPLETED),
        tap(() => {
          this.authService.updateCompleted();
        })
      ),
    { dispatch: false }
  );

  find$ = createEffect(() =>
    this.actions$.pipe(
      ofType(USER_FIND),
      withLatestFrom(this.store.select(selectAuth)),
      filter(([_, user]) => !!user && !!user?.clienteId),
      mergeMap(([_, user]) => {
        this.store.dispatch(USER_UPDATE_LOADING({ loading: true }));

        return this.authService.find(user?.clienteId).pipe(
          mergeMap(({ data }) => {
            const [user] = data as Client[];

            return [
              USER_UPDATE_LOADING({ loading: false }),
              CLIENT_FIND_SUCCESS({ client: user }),
            ];
          }),
          catchError((error) =>
            of(
              USER_FAILED({
                error: error.error?.message || 'Error loading user info',
              })
            )
          )
        );
      })
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(USER_UPDATE),
      withLatestFrom(
        this.store.select(selectUserForm),
        this.store.select(selectAuth)
      ),
      filter(([_, userForm, auth]) => !!userForm && !!auth),
      map(([_, userForm, auth]) => {
        return { clientForm: userForm as unknown as ClientUserForm, auth: auth as Authentication };
      }),
      mergeMap(({ clientForm, auth }) => {
        this.store.dispatch(USER_UPDATE_LOADING({ loading: true }));
        return this.authService.update(clientForm, auth).pipe(
          mergeMap(() => {
            return [
              USER_FIND(),
              USER_UPDATE_LOADING({ loading: false }),
              NOTIFICATION_SHOW({
                notification: {
                  title: NotificationEnum.UpdateUser,
                  type: NotificationEnum.Success,
                  detail: NotificationEnum.UserUpdateSuccessfully,
                },
              }),
            ];
          }),
          catchError((error) =>

            of(
              NOTIFICATION_SHOW({
                notification: {
                  title: NotificationEnum.Error,
                  type: NotificationEnum.Error,
                  detail: error.error?.message || NotificationEnum.ErrorUpdatingUser,
                },
              }),
              USER_FAILED({
                error: error.error?.message || NotificationEnum.ErrorUpdatingUser,
              })
            )
          )
        );
      })
    )
  );
}
